import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import { SIGNIN } from '../../../routes/paths';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FieldText from '../../components/general/fields/fieldText';
import { MasterMessage } from '../../../masters/masterMessages';
import { asyncAuthRegister } from '../../../services/auth.service';
import { Copyright } from '../../components/copyright/copyright';
import { ToastSwal } from '../../components/general/toastSwal/toastSwal';

export default function SignUp() {
    const navigate = useNavigate();
    const {
        messageRegister,
        messageErrorRegisterCode,
        messageErrorRegisterUserName,
        messageErrorRegister,
    } = MasterMessage.auth.register;

    const formik = useFormik({
        initialValues: {
            code: '',
            name: '',
            surname: '',
            username: '',
            password: '',
            password_retry: '',
        },
        validationSchema: Yup.object({
            code: Yup.number()
                .typeError('El código debe ser un número')
                .required('El código es obligatorio'),
            name: Yup.string().required('El nombre es obligatorio'),
            surname: Yup.string().required('El apellido es obligatorio'),
            username: Yup.string()
                .email('El correo es invalido')
                .required('El correo de usuario es obligatorio')
                .test('Validate Email', 'El correo es inválido', (value) => {
                    const re =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return re.test(String(value).toLowerCase());
                }),
            password: Yup.string()
                .required('La contraseña es obligatoria')
                .min(8, 'La contraseña debe tener al menos 8 caracteres'),
            password_retry: Yup.string()
                .required('La confirmación de la contraseña es obligatoria')
                .oneOf([Yup.ref('password')], 'Las contraseñas no coinciden'),
        }),
        onSubmit: (formAuthRegister) => {
            asyncAuthRegister(formAuthRegister).then((body: any) => {
                if (body.message !== undefined) {
                    ToastSwal.fire({
                        text:
                            body.message === 'AUTH-NO-REGISTER-CODE'
                                ? messageErrorRegisterCode
                                : body.message === 'AUTH-NO-REGISTER-EMAIL'
                                    ? messageErrorRegisterUserName
                                    : messageErrorRegister,
                        icon: 'error',
                        iconColor: '#1d1d1d',
                    });
                } else {
                    navigate(SIGNIN);
                    ToastSwal.fire({
                        text: messageRegister,
                        icon: 'success',
                        iconColor: '#1d1d1d',
                    });
                }
            });
        },
    });

    return (
        <>
            <Box
                sx={{
                    margin: '0 auto auto',
                    width: { xs: 320, sm: 460, md: 850, lg: 900 },
                    height: '100%',
                    boxShadow: 5,
                    borderRadius: 3,
                    px: 3,
                    py: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Avatar
                    sx={{
                        m: 1,
                        bgcolor: 'primary.main',
                        width: 60,
                        height: 60,
                    }}
                    variant="rounded"
                >
                    <LockOutlinedIcon fontSize="large" />
                </Avatar>
                <Typography component="h1" variant="h5">
                    Registrarse
                </Typography>
                <Box
                    component="form"
                    noValidate
                    onSubmit={formik.handleSubmit}
                    sx={{ mt: 3 }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={6}>
                            <FieldText
                                label="Código"
                                name="code"
                                required={true}
                                touched={formik.touched.code}
                                errors={formik.errors.code}
                                value={formik.values.code}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FieldText
                                label="Nombre"
                                name="name"
                                required={true}
                                touched={formik.touched.name}
                                errors={formik.errors.name}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FieldText
                                label="Apellido"
                                name="surname"
                                required={true}
                                touched={formik.touched.surname}
                                errors={formik.errors.surname}
                                value={formik.values.surname}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FieldText
                                label="Correo Usuario"
                                name="username"
                                required={true}
                                touched={formik.touched.username}
                                errors={formik.errors.username}
                                value={formik.values.username}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FieldText
                                label="Contraseña"
                                name="password"
                                required={true}
                                type="password"
                                touched={formik.touched.password}
                                errors={formik.errors.password}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FieldText
                                label="Confirmar Contraseña"
                                name="password_retry"
                                required={true}
                                type="password"
                                touched={formik.touched.password_retry}
                                errors={formik.errors.password_retry}
                                value={formik.values.password_retry}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item className="text-center" xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                className="text-center"
                                sx={{ mt: 2, mb: { xs: 2, md: 1 } }}
                            >
                                Registrarse
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container justifyContent="flex-end">
                        <Grid item>
                            <Link
                                onClick={() => navigate(SIGNIN)}
                                variant="body2"
                                type="submit"
                            >
                                ¿Ya tienes una cuenta? Iniciar sesión
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Copyright sx={{ position: 'relative', mt: 3 }} />
        </>
    );
}
